import { useNavigate } from "react-router-dom";
import {
  useLoginCodeSelector,
  useStoreBrnDSSelector,
  useModulesSelector,
  useCompanyLogoSelector,
} from "../store/selectors/userSelector";
import { useState } from "react";
import { useDataSourceApi } from "../store/api/hooks/dataSourceApiHook";
import { useUserApi } from "../store/api/hooks/userApiHook";
import { useDispatch } from "react-redux";
import { ACTIONS, URLS, STORAGE } from "../_config";
import toast from "react-hot-toast";
import Spinner from "../components/iconSvgs/spinner";

// Module IDs mapping
const MODULE_IDS = {
  SENTIMENT_DASHBOARD: 1,
  MENU_DESIGN: 2,
  MARKETING_AUTOMATION: 3,
  SMART_INVENTORY: 4,
  ANALYTICS: 5,
};

const HUBWALLET_MODULE_URL = process.env.REACT_APP_REDIRECT_URL || '';

const ModuleSelection = () => {
  const [isOnSubmitLoading, setOnSubmitLoading] = useState<boolean>(false);

  const navigate = useNavigate();
  const loginCode = useLoginCodeSelector();
  const branchData = useStoreBrnDSSelector();
  const dataSourceAPI = useDataSourceApi();
  const userAPI: any = useUserApi();
  const dispatch = useDispatch();
  const modules = useModulesSelector();
  const companyLogo = useCompanyLogoSelector();

  const handleLogout = () => {
    try {
      userAPI.logout({}, (_msg: any, _resp: any) => {
        localStorage.removeItem(STORAGE);
        dispatch({ type: ACTIONS.USER.LOGOUT, payload: {} });
        dispatch({ type: ACTIONS.PUSHER.RESET_DATA, payload: [] });
        navigate(URLS.USER.LOGIN);
        toast.success('User Logged Out Successfully');
      }, (_err: any, resp: any) => {
        if (resp?.message === 'User logged out, session removed') {
          localStorage.removeItem(STORAGE);
          dispatch({ type: ACTIONS.USER.LOGOUT, payload: {} });
          dispatch({ type: ACTIONS.PUSHER.RESET_DATA, payload: [] });
          navigate(URLS.USER.LOGIN);
          toast.success('User Logged Out Successfully');
        }
      });
    } catch (e: any) {
      toast.error(e?.message);
    }
  };

  // Check if a module should be shown
  // If modules is null/undefined, show all modules
  // Otherwise, only show modules that are in the array
  const isModuleVisible = (moduleId: number): boolean => {
    if (!modules || modules.length === 0) {
      return true; // Show all if modules is null or empty
    }
    return modules.includes(moduleId);
  };

  const onSubmit = () => {
    setOnSubmitLoading(true);
    const param: any = {
      branch_id: branchData?.branch_id,
    };
    try {
      setTimeout(() => {
        dataSourceAPI.getDataSource(
          param,
          (_msg: any, resp: any) => {
            if (resp?.code === 200) {
              if (resp.data.length > 0) {
                let inPending: any = null;
                let failure: any = null;
                const completed = resp?.data.find(
                  (item: any) => item.last_fetch_status === "completed"
                );
                if (!completed) {
                  inPending = resp?.data.find(
                    (item: any) => item.last_fetch_status === "pending"
                  );
                  if (!inPending) {
                    failure = resp?.data.find(
                      (item: any) => item.last_fetch_status !== 'completed' && item.last_fetch_status !== 'pending'
                    );
                  }
                }
                if (completed) {
                  dispatch({
                    type: ACTIONS.STORE.GET_BRANCH,
                    payload: completed,
                  });
                  navigate(URLS.DASHBOARD);
                } else if (inPending) {
                  dispatch({
                    type: ACTIONS.STORE.GET_BRANCH,
                    payload: inPending,
                  });
                  navigate(URLS.DATASOURCES.LIST);
                } else {
                  dispatch({
                    type: ACTIONS.STORE.GET_BRANCH,
                    payload: failure,
                  });
                  navigate(URLS.DATASOURCES.LIST);
                }
              }
            } else if (resp?.code === 404) {
              // toast.error(resp?.message);
              const payload = {
                source_type: null,
                url: null,
                last_fetch_date: null,
                created_at: null,
                ds_id: null,
                store_id: branchData?.store_id,
                branch_id: branchData?.branch_id,
                branch_name: branchData?.branch_name,
                snapshot_id: null,
                last_fetch_status: null,
              };
              dispatch({
                type: ACTIONS.STORE.GET_BRANCH,
                payload: payload,
              });
              navigate(URLS.DATASOURCES.CREATE);
            }
          },
          (error: any, resp: any) => {
            console.log("error =>", error, resp);
            if (resp?.detail?.code === 404) {
              // toast.error(resp?.detail?.message);
              const payload = {
                source_type: null,
                url: null,
                last_fetch_date: null,
                created_at: null,
                ds_id: null,
                store_id: branchData?.store_id,
                branch_id: branchData?.branch_id,
                branch_name: branchData?.branch_name,
                snapshot_id: null,
                last_fetch_status: null,
              };
              // console.log('payload =>,', payload);
              dispatch({
                type: ACTIONS.STORE.GET_BRANCH,
                payload: payload,
              });
              navigate(URLS.DATASOURCES.CREATE);
              // navigate(URLS.SETTINGS);
            } else {
              // toast.error("Something went wrong");
            }
          }
        );
        setOnSubmitLoading(false);
      }, 5000);
    } catch (err: any) {
      toast.error(err);
      setOnSubmitLoading(false);
    }
  };

  const handleOnClick = (url: string, isSentimentAnalysis: boolean) => {
    if (isSentimentAnalysis) {
      onSubmit();
      // navigate(url);
    } else {
      // window.open(url);
      window.location.href = url;
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-tl from-blue-50 via-blue-100 to-white overflow-hidden">
      {isOnSubmitLoading && (
        <div className="absolute inset-0 bg-gray-700 bg-opacity-50 flex justify-center items-center z-50">
          <Spinner className="self-center z-50" />
        </div>
      )}
      <header className="flex justify-between items-center p-6 bg-[#E9F0FF] border-b-2 border-gray-300">
        <div className="flex items-center space-x-3">
          <img src={companyLogo || "/assets/images/logo.png"} alt="company logo" className="h-12" />
        </div>
        <button
          onClick={handleLogout}
          className="px-4 py-2 text-sm text-red-500 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
        >
          Logout
        </button>
      </header>

      <main className="flex flex-col justify-center items-center p-8">
        <p className="text-lg text-gray-600 mb-12">Module Dashboard</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-6 w-full max-w-4xl">
          {isModuleVisible(MODULE_IDS.SENTIMENT_DASHBOARD) && (
            <div
              className="bg-white shadow-lg rounded-lg px-8 py-6 flex items-center space-x-3 group transition transform hover:scale-105 flex-col w-full min-h-[150px] cursor-pointer"
              onClick={() => handleOnClick("/", true)}
            >
              <div className="text-grey p-2 rounded-full bg-[#E9F1FF]">
                <img
                  src="/assets/images/sentiment-analysis.png"
                  alt=""
                  className="h-[65px]"
                />
              </div>
              <span className="text-lg font-medium text-gray-800">
                Sentiment Dashboard
              </span>
            </div>
          )}
          {isModuleVisible(MODULE_IDS.MENU_DESIGN) && (
            <div
              className="bg-white shadow-lg rounded-lg px-8 py-6 flex items-center space-x-3 group transition transform hover:scale-105 flex-col w-full min-h-[150px] cursor-pointer"
              onClick={() =>
                handleOnClick(
                  `${HUBWALLET_MODULE_URL}?loginCode=${loginCode}&branchId=${branchData?.branch_id}`,
                  false
                )
              }
            >
              <div className="text-grey p-2 rounded-full bg-[#E9F1FF]">
                <img
                  src="/assets/images/web-design.png"
                  alt=""
                  className="h-[65px]"
                />
              </div>
              <span className="text-lg font-medium text-gray-800">
                Menu Design
              </span>
            </div>
          )}
          {isModuleVisible(MODULE_IDS.MARKETING_AUTOMATION) && (
            <div
              className="bg-white shadow-lg rounded-lg px-8 py-6 flex items-center space-x-3 group transition transform hover:scale-105 flex-col w-full min-h-[150px] cursor-pointer"
              onClick={() =>
                handleOnClick(
                  `${HUBWALLET_MODULE_URL}?type=calendar&loginCode=${loginCode}&branchId=${branchData?.branch_id}`,
                  false
                )
              }
            >
              <div className="text-grey p-2 rounded-full bg-[#E9F1FF]">
                <img
                  src="/assets/images/marketing-automation.png"
                  alt=""
                  className="h-[65px]"
                />
              </div>
              <span className="text-lg font-medium text-gray-800">
                Marketing Automation
              </span>
            </div>
          )}
          {isModuleVisible(MODULE_IDS.SMART_INVENTORY) && (
            <div
              className="bg-white shadow-lg rounded-lg px-8 py-6 flex items-center space-x-3 group transition transform hover:scale-105 flex-col w-full min-h-[150px] cursor-pointer"
              onClick={() =>
                handleOnClick(
                  `${HUBWALLET_MODULE_URL}?type=smartinventory&loginCode=${loginCode}&branchId=${branchData?.branch_id}`,
                  false
                )
              }
            >
              <div className="text-grey p-2 rounded-full bg-[#E9F1FF]">
                <img
                  src="/assets/images/smart-inventory.png"
                  alt=""
                  className="h-[65px]"
                />
              </div>
              <span className="text-lg font-medium text-gray-800">
                Smart Inventory
              </span>
            </div>
          )}
          {isModuleVisible(MODULE_IDS.ANALYTICS) && (
            <div
              className="bg-white shadow-lg rounded-lg px-8 py-6 flex items-center space-x-3 group transition transform hover:scale-105 flex-col w-full min-h-[150px] cursor-pointer"
              onClick={() =>
                handleOnClick(
                  `${HUBWALLET_MODULE_URL}?type=analytics&loginCode=${loginCode}&branchId=${branchData?.branch_id}`,
                  false
                )
              }
            >
              <div className="text-grey p-2 rounded-full bg-[#E9F1FF]">
                <img
                  src="/assets/images/analytics.png"
                  alt=""
                  className="h-[65px]"
                />
              </div>
              <span className="text-lg font-medium text-gray-800">
                Analytics Module
              </span>
            </div>
          )}
        </div>
      </main>

      {/* Background Circles */}
      <div className="absolute top-0 left-0 w-20 h-20 bg-blue-100 rounded-full opacity-20 animate-pulse"></div>
      <div className="absolute top-20 left-40 w-28 h-28 bg-blue-200 rounded-full opacity-30 animate-pulse"></div>
      <div className="absolute top-40 right-20 w-36 h-36 bg-blue-300 rounded-full opacity-40 animate-pulse"></div>
    </div>
  );
};

export default ModuleSelection;
